/**
 * Route copy for `src/routes/prospection` (keys: `routes.prospection.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  accountIntelligencePage: {
    notAvailable: 'Not available',
    companyProfile: 'Company profile',
    legalName: 'Legal name',
    industry: 'Industry',
    estimatedSize: 'Estimated size',
    headquarters: 'Headquarters',
    yearFounded: 'Year founded',
    classification: 'Classification',
    fitFitScore5: 'Fit {{fit_score}}/5',
    organizationType: 'Organization type',
    industryVertical: 'Industry vertical',
    marketSegment: 'Market segment',
    visionMission: 'Vision & mission',
    vision: 'Vision:',
    mission: 'Mission:',
    sourcePublicWebsiteProvided: 'Source: public website provided',
    sourceInferredAiHypothesis: 'Source: inferred (AI hypothesis)',
    valueProposition: 'Value proposition',
    predictedNeeds: 'Predicted needs',
    priorityPriority: 'Priority {{priority}}',
    serviceProposal: 'Service proposal',
    expectedImpact: 'Expected impact:',
    competitiveAnalysis: 'Competitive analysis',
    noKnownCompetitorProvided: 'No known competitor provided.',
    positioning: 'Positioning:',
    strength: 'Strength:',
    weakness: 'Weakness:',
    differentiationAngle: 'Differentiation angle:',
    marketAnalysis: 'Market analysis',
    marketSize: 'Market size:',
    trends: 'Trends',
    opportunities: 'Opportunities',
    risks: 'Risks',
    accountStrategy: 'Account strategy',
    messageAngle: 'Message angle:',
    keyTalkingPoints: 'Key talking points',
    nextActions: 'Next actions',
    companyName: 'Company name',
    website: 'Website',
    businessGoal: 'Business goal',
    industry2: 'Industry',
    notesAlreadyKnown: 'Notes already known',
    websiteExcerptPasted: 'Website excerpt (pasted)',
    knownCompetitors: 'Known competitors',
    enterTheNameOfTheCompany: 'Enter the name of the company to analyze.',
    accountIntelligenceAnalysisUnavailableFe: 'Account Intelligence analysis unavailable — feature not deployed or AI temporarily unavailable.',
    thisFileCannotBeFoundOr: 'This file cannot be found or the feature is unavailable.',
    prospectingAccountIntelligence: 'Prospecting · Account Intelligence',
    describeATargetCompanyToGet: 'Describe a target company to get a complete intelligence brief — profile, classification, vision/mission, value proposition, predicted needs, service proposal, competitive analysis, market and strategy.',
    eGAcmeCorporation: 'E.g. Acme Corporation',
    websiteOptional: 'Website (optional)',
    httpsExampleCom: 'https://example.com',
    businessGoalOptional: 'Business goal (optional)',
    eGProposeAPartnershipPresent: 'E.g. propose a partnership, present our offer…',
    industryOptional: 'Industry (optional)',
    eGFinancialServices: 'E.g. financial services',
    notesAlreadyKnownCrmEtcOptional: 'Notes already known (CRM, etc. — optional)',
    freeNotesAlreadyKnownAboutThe: 'Free notes already known about the company…',
    websiteExcerptAlreadyRetrievedOptional: 'Website excerpt already retrieved (optional)',
    pasteAnExcerptAlreadyObtainedThrough: 'Paste an excerpt already obtained through an authorized tool (never unauthorized scraping) — never made up by this service.',
    textExcerptFromTheCompanyS: 'Text excerpt from the company\'s public website…',
    competitorsAlreadyIdentifiedOptional: 'Competitors already identified (optional)',
    oneNamePerLineOrComma: 'One name per line or comma-separated — never made up by the AI.',
    eGCompetitorACompetitorB: 'E.g. Competitor A, Competitor B',
    saveThisFile: 'Save this file',
    analysisInProgress: 'Analysis in progress…',
    analyzeTheAccount: 'Analyze the account',
    yourRoleCannotRunAnAnalysis: 'Your role cannot run an analysis (the action uses AI credits) — contact an administrator.',
    analyzingTheAccount: 'Analyzing the account…',
    enterATargetCompanyAboveThen: 'Enter a target company above, then click “Analyze the account”.',
    deleteTheFileTitle: 'Delete the file',
    deleteTheFileMessage: 'Delete the file “{{name}}”?',
    deleteTheFileConfirm: 'Delete',
    deleteTheFileLabel: 'Delete the file {{name}}',
    deleteTheFileFailed: 'Could not delete the file.',
    savedFiles: 'Saved files',
    loading: 'Loading…',
    refresh: 'Refresh',
    loadingFiles: 'Loading files…',
    featureNotDeployedTheFileList: 'Feature not deployed — the file list is unavailable for now.',
    noSavedFileYet: 'No saved file yet.',
    loadingTheFile: 'Loading the file…',
  },
  addToCrmModal: {
    discoveryV: 'Discovery – {{v}}',
    discovery: 'Discovery',
    totalRecordSAdded: '{{total}} record(s) added.',
    addingToTheCrmFailedTry: 'Adding to the CRM failed. Try again.',
    addLengthCandidateSToThe: 'Add {{length}} candidate(s) to the CRM',
    close: 'Close',
    cancel: 'Cancel',
    adding: 'Adding…',
    addToTheCrm: 'Add to the CRM',
    contactSWillBeCreatedThen: 'contact(s) will be created, then exported to the CRM (automatic deduplication).',
    nonPersonEntityIesCompanyAgency: 'non-person entity(ies) (company/agency/organization…) will be created, then exported to the CRM as accounts.',
    batchNameInternalB2bList: 'Batch name (internal B2B list)',
    keptAsATraceVisibleIn: 'Kept as a trace, visible in B2B base › My lists.',
    antiBounceShieldVerifyEmailsBefore: 'Anti-bounce shield — verify emails before importing',
    discoveryCandidatesHaveNoEmailUntil: 'Discovery candidates have no email until they are enriched. Paste here the addresses you plan to import / contact for this batch: they are verified (syntax + MX + SMTP) and only deliverable addresses should go to the CRM or a sequence.',
    contactsexportedContactSExportedToThe: '✓ {{contactsExported}} contact(s) exported to the CRM',
    contactsalreadyincrmAlreadyInTheCrmDedup: '· {{contactsAlreadyInCrm}} already in the CRM (deduplicated)',
    contactsfailedExportFailureS: '· {{contactsFailed}} export failure(s)',
    companiesexportedEntityIesExportedToThe: '✓ {{companiesExported}} entity(ies) exported to the CRM',
    companiesfailedFailureS: '· {{companiesFailed}} failure(s)',
  },
  bounceShieldPanel: {
    deliverable: 'Deliverable',
    risky: 'Risky',
    catchAll: 'Catch-all',
    invalid: 'Invalid',
    undetermined: 'Undetermined',
    enterAtLeastOneEmailAddress: 'Enter at least one email address to verify.',
    theVerificationFailedTryAgain: 'The verification failed. Try again.',
    lengthDeliverableAddressEsKept: '{{length}} deliverable address(es) kept',
    lengthDiscarded: ' · {{length}} discarded',
    addressesToVerify: 'Addresses to verify',
    pasteEmailAddressesSeparatedByCommas: 'Paste email addresses (separated by commas, semicolons or line breaks)',
    lengthAddressEsDetected: '{{length}} address(es) detected.',
    noAddressDetectedYet: 'No address detected yet.',
    syntaxMxSmtpVerificationBeforeImport: 'Syntax + MX + SMTP verification before import — invalid ones never go to the CRM.',
    checking: 'Checking…',
    verifyTheAddresses: 'Verify the addresses',
    keepTheDeliverableOnesLength: 'Keep the deliverable ones ({{length}})',
    checkingDeliverability: 'Checking deliverability…',
    discarded: 'discarded',
    risky2: 'risky',
    undetermined2: 'undetermined',
    riskyAddressesAreKeptInconclusiveSmtp: '“Risky” addresses are kept (inconclusive SMTP probe, often because outbound port 25 is blocked) — never silently discarded.',
    deliverable2: 'deliverable',
    valid: 'valid',
    catchAll2: 'catch-all',
    invalid2: 'invalid',
  },
  discoveryPage: {
    whatAreYouLookingFor: 'What are you looking for?',
    postalCode: 'Postal code',
    city: 'City',
    countryIso2: 'Country (ISO-2)',
    describeWhatYouAreLookingFor: 'Describe what you are looking for (name, topic, goal).',
    selectAtLeastOneSourceOr: 'Select at least one source, or choose “All sources”.',
    theSearchFailedTryAgain: 'The search failed. Try again.',
    prospectingDiscovery: 'Prospecting · Discovery',
    findTargetedPeopleCompaniesAgenciesOr: 'Find targeted people, companies, agencies or organizations by geographic area, through official commercial sources.',
    eGAccountingFirmsQuebecCity: 'E.g. accounting firms, Quebec City hall, independent marketing agencies…',
    entityTypesOptionalAllByDefault: 'Entity types (optional — all by default)',
    regionProvince: 'Region / province',
    caFr: 'CA, FR…',
    continent: 'Continent',
    all: 'All',
    latitudePoint: 'Latitude (point)',
    longitudePoint: 'Longitude (point)',
    radiusKm: 'Radius (km)',
    requiresAPointOrAPostal: '— requires a point or a postal code',
    sources: 'Sources',
    allAvailableSources: 'All available sources',
    select: 'Select…',
    free: 'free',
    keyRequired: 'key required',
    optIn: 'opt-in',
    keyRequiredTheSourceIsSkipped: '“Key required”: the source is skipped (status',
    skippedNoKey: 'skipped_no_key',
    untilAnAdministratorConfiguresItsKey: ') until an administrator configures its key/API — see the source details below the results. “Opt-in”: the source is only queried if you check it here (never in “All sources”).',
    advancedCriteriaApollo: 'Advanced criteria (Apollo)',
    theseCriteriaOnlyRefineTheApollo: 'These criteria only refine the Apollo sources. The',
    company: ' “company”',
    criteriaApplyToApolloCompaniesAnd: 'criteria apply to “Apollo — companies” and “Apollo — people search”. The',
    person: ' “person”',
    criteriaOnlyTakeEffectWithThe: 'criteria only take effect with the opt-in source “Apollo — people search”: check it in the sources above.',
    companyCriteria: 'Company criteria',
    numberOfEmployees: 'Number of employees',
    minRevenue: 'Min revenue',
    maxRevenue: 'Max revenue',
    industryApolloTagId: 'Industry — Apollo tag ID',
    commaSeparatedIdS: 'Comma-separated ID(s)',
    technologiesUsed: 'Technologies used',
    salesforceReact: 'salesforce, react…',
    serviceVisionMissionKeywords: 'Service / vision / mission keywords',
    taxAdviceSustainableDevelopment: 'tax advice, sustainable development…',
    personCriteriaApolloPeopleSearchSource: 'Person criteria — “Apollo — people search” source (opt-in)',
    functionRole: 'Function / role',
    marketingDirectorVpOfSales: 'marketing director, VP of sales…',
    schoolAlmaMater: 'School / alma mater',
    hecMontreal: 'HEC Montréal…',
    skillsKeywords: 'Skills / keywords',
    growthSeo: 'growth, SEO…',
    locationsPeople: 'Locations (people)',
    montrealQuebec: 'Montreal, Quebec…',
    seniority: 'Seniority',
    maxResults: 'Max results',
    searching: 'Searching…',
    startTheDiscovery: 'Start the discovery',
    yourRoleCannotStartADiscovery: 'Your role cannot start a discovery (the action uses credits) — contact an administrator.',
    searchingTheSelectedSources: 'Searching the selected sources…',
    noSourceIsConfiguredForThis: 'No source is configured for this search. Configure at least one API key (see the source details above) to run a real discovery.',
    noResultsForTheseCriteriaBroaden: 'No results for these criteria. Broaden the geographic area or the entity types.',
    selectAllLength: 'Select all ({{length}})',
    sizeSelected: '{{size}} selected',
    addToTheCrm: 'Add to the CRM',
    selectName: 'Select {{name}}',
    vFromTheSearchedPoint: '{{v}} from the searched point',
    source: 'Source:',
    confidenceV: 'Confidence {{v}}',
    runASearchToDiscoverTargeted: 'Run a search to discover targeted prospects and companies.',
  },
  inboundRoutersPage: {
    enterANameAConditionAnd: 'Enter a name, a condition and an assignment rule.',
    routingRuleSaved: 'Routing rule saved.',
    prospectingInboundLeadRouting: 'Prospecting · Inbound lead routing',
    inboundLeadRoutingRulesWhenA: 'Inbound lead routing rules: when a condition matches, the lead is sent to a target (team, queue or owner).',
    newRule: 'New rule',
    name: 'Name',
    eGEnterpriseLeadsSalesEu: 'E.g. Enterprise leads → Sales EU',
    condition: 'Condition',
    eGIndustryTechnology: 'E.g. industry=technology',
    fieldValue: 'field=value',
    assignmentRule: 'Assignment rule',
    firstCreateAnAssignmentRuleIn: 'First create an assignment rule in CRM › Settings.',
    leadDistributionRoundRobinByTag: 'Lead distribution (round-robin, by tag, territory…)',
    choose: '— Choose —',
    priority: 'Priority',
    smallerEvaluatedFirst: 'Smaller = evaluated first',
    saving: 'Saving…',
    addTheRule: 'Add the rule',
    ruleCreationComingSoonNotConfigured: 'Rule creation — coming soon / not configured on this environment.',
    configuredRules: 'Configured rules',
    loadingRules: 'Loading rules…',
    inboundLeadRoutingComingSoonNot: 'Inbound lead routing — coming soon / not configured on this environment.',
    noRoutingRuleConfiguredAddA: 'No routing rule configured. Add a rule above to automatically route inbound leads matching a condition.',
    disabled: 'Disabled',
    noAssignmentRule: 'no assignment rule',
    priorityPriority: 'Priority {{priority}}',
  },
  jobChangeAlertsPage: {
    unexpectedError: 'Unexpected error.',
    prospectingJobChangeAlerts: 'Prospecting · Job change alerts',
    jobChangeSignalsReportedOnYour: 'Job change signals reported on your contacts (CRM feed), and an on-demand scan of your saved contacts\' current employer.',
    jobChangeSignalFeed: '“Job change” signal feed',
    loadingAlerts: 'Loading alerts…',
    signalFeedComingSoonNotConfigured: 'Signal feed — coming soon / not configured on this environment.',
    noJobChangeAlertYetAlerts: 'No job change alert yet. Alerts appear here as soon as a “job change” signal is recorded for one of your contacts.',
    contact: 'contact',
    unresolvedContact: 'unresolved contact',
    company: 'company',
    sourceSource: 'source {{source}}',
    scanTheCurrentEmployerOfSaved: 'Scan the current employer of saved contacts',
    reChecksYourContactsEmployerEnrichment: 'Re-checks your contacts\' employer (enrichment provider configured by the administrator) and detects a change versus the saved company.',
    analysisInProgress: 'Analysis in progress…',
    startTheScan: 'Start the scan',
    actionReservedForAnEditorAdministrator: 'Action reserved for an editor/administrator role.',
    scanInProgress: 'Scan in progress…',
    jobChangeScanComingSoonNot: 'Job change scan — coming soon / not configured on this environment.',
    contactsAnalyzed: 'Contacts analyzed',
    changesDetected: 'Changes detected',
    skippedProviderKeyMissing: 'Skipped (provider key missing)',
    noEmployerChangeDetectedInThis: 'No employer change detected in this batch.',
    unknownCompany: 'Unknown company',
  },
  jobChangeScan: {
    actionReservedForAnEditorAdministrator: 'Action reserved for an editor/administrator role — your role is read-only.',
    unexpectedError: 'Unexpected error.',
  },
  offeringsPage: {
    executiveCLevel: 'Executive (C-level)',
    vp: 'VP',
    director: 'Director',
    manager: 'Manager',
    senior: 'Senior',
    junior: 'Junior',
    owner: 'Owner',
    partner: 'Partner',
    removeV: 'Remove {{v}}',
    add: 'Add…',
    theNameIsRequired: 'The name is required.',
    theSlugCanOnlyContainLowercase: 'The slug can only contain lowercase letters, digits and hyphens.',
    couldNotSave: 'Could not save.',
    editTheOfferName: 'Edit the offer — {{name}}',
    newOffer: 'New offer',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    prefill: 'Prefill:',
    offerName: 'Offer name *',
    kingdomConnection: 'Kingdom Connection',
    slug: 'Slug',
    crmLabelOfferingSlug: 'CRM label: offering:{slug}',
    description: 'Description',
    valueProposition: 'Value proposition',
    usedForTheOutreachAndTo: 'Used for the outreach and to explain the legitimate interest.',
    idealCustomerIcp: 'Ideal customer (ICP)',
    industries: 'Industries',
    realEstateReligiousInstitutions: 'Real Estate, Religious Institutions…',
    enterOrCommaToAdd: 'Enter or comma to add.',
    rolesTitles: 'Roles / titles',
    pastorRealEstateBroker: 'Pastor, Real estate broker…',
    seniority: 'Seniority',
    companySizeEmployees: 'Company size (employees)',
    countryIsoCode: 'Country (ISO code)',
    ca: 'CA',
    regionProvince: 'Region / province',
    qc: 'QC',
    city: 'City',
    montreal: 'Montreal',
    keywords: 'Keywords',
    churchRealEstate: 'church, real estate…',
    theFirstKeywordIsUsedAs: 'The first keyword is used as a text search in the directory.',
    exclusions: 'Exclusions',
    competitorsDomainsToExclude: 'Competitors, domains to exclude…',
    entityTypesCompanies: 'Entity types (companies)',
    firmOrganizationAgency: 'firm, organization, agency…',
    offerActive: 'Offer active',
    imported: 'Imported',
    alreadyImported: 'Already imported',
    notFound: 'Not found',
    error: 'Error',
    invalid: 'Invalid',
    prospectingFailed: 'Prospecting failed.',
    noSeedsAddBestCustomersTo: 'No seeds: add “best customers” to the offer to find similar profiles.',
    noneOfTheOfferSBest: 'None of the offer\'s best customers was found in the directory.',
    theSimilarProfileSearchFailed: 'The similar-profile search failed.',
    importedProspectSImportedIntoThe: '{{imported}} prospect(s) imported into the CRM (label offering:{{slug}}).',
    theImportFailed: 'The import failed.',
    prospectName: 'Prospect — {{name}}',
    close: 'Close',
    analysisRecommendationsForThisOffer: 'Analysis & recommendations for this offer',
    target: 'Target',
    contactsPeople: 'Contacts (people)',
    companies: 'Companies',
    source: 'Source',
    myB2bDirectory: 'My B2B directory',
    externalDiscoveryPaid: 'External discovery (paid)',
    directoryDiscovery: 'Directory + discovery',
    limit1200: 'Limit (1–200)',
    searching: 'Searching…',
    prospect: 'Prospect',
    readOnlyProspectingRequiresARole: 'Read-only: prospecting requires a role with write permissions.',
    externalDiscoveryQueriesLicensedProvider: 'External discovery queries licensed providers and uses credits. Only the configured official sources are used.',
    findProfilesSimilarToMyBest: 'Find profiles similar to my best customers',
    vSeedSSaved: '({{v}} seed(s) saved)',
    discoveryUnavailable: 'Discovery unavailable',
    noSourceAnsweredTheDirectoryResult: '. No source answered — the directory result remains valid.',
    similarProfilesLengthSeedsResolvedSeed: 'Similar profiles: {{length}} ({{seeds_resolved}} seed(s) resolved)',
    directoryTotal: 'Directory: {{total}}',
    excludedExcluded: ' ({{excluded}} excluded)',
    discoveryTotal: 'Discovery: {{total}}',
    enrichBeforeImportUsesCredits: 'Enrich before import (uses credits)',
    importing: 'Importing…',
    importSizeIntoTheCrm: 'Import {{size}} into the CRM',
    noProspectMatchesTheIcpBroaden: 'No prospect matches the ICP. Broaden the industries, roles or geographic area, or try external discovery.',
    selectAll: 'Select all',
    prospect2: 'Prospect',
    detail: 'Detail',
    location: 'Location',
    contact: 'Contact',
    selectTitle: 'Select {{title}}',
    directory: 'Directory',
    importedOutOfTotalSource: 'imported out of {{total}} — source',
    label: ', label',
    offeringSlug: 'offering:{{slug}}',
    legalBasisPendingComplianceServiceUnavai: 'legal basis pending (compliance service unavailable): sending stays blocked until it is recorded.',
    role: 'Role',
    keyword: 'Keyword',
    icpNotDefinedEditTheOffer: 'ICP not defined — edit the offer to target your prospects.',
    couldNotLoadTheOffers: 'Could not load the offers.',
    deleteTheOfferNameContactsAlready: 'Delete the offer “{{name}}”? Contacts already imported stay in the CRM.',
    offerDeleted: 'Offer deleted.',
    deletionFailed: 'Deletion failed.',
    offerLabelCreatedAdjustTheIcp: 'Offer “{{label}}” created — adjust the ICP as needed.',
    couldNotCreateDoesItAlready: 'Could not create (does it already exist?).',
    offersProspecting: 'Offers & prospecting',
    describeEachOfYourProductsOr: 'Describe each of your products or brands with its ideal customer (ICP), then start prospecting: targeted results, imported into the CRM labeled by offer.',
    quickStart: 'Quick start:',
    creating: 'Creating…',
    noOffersCreateYourFirstOffer: 'No offers. Create your first offer (or use a quick start above) to define who to prospect.',
    createAnOffer: 'Create an offer',
    active: 'Active',
    inactive: 'Inactive',
    offerInactive: 'Offer inactive',
    edit: 'Edit',
    delete: 'Delete',
    offerSaved: 'Offer saved.',
    industry: 'Industry',
  },
  prospectionCalendarPage: {
    manual: ' · manual',
    objective: 'Objective',
    contactIds: 'Contact IDs',
    groupSegment: 'Group / segment',
    numberOfTouches: 'Number of touches',
    describeTheSequenceSBusinessGoal: 'Describe the sequence\'s business goal.',
    enterAtLeastOneContactIdentifier: 'Enter at least one contact identifier.',
    enterAGroupOrSegmentIdentifier: 'Enter a group or segment identifier.',
    generationFailedTryAgain: 'Generation failed. Try again.',
    schedulingFailedTryAgain: 'Scheduling failed. Try again.',
    prospectingCalendar: 'Prospecting · Calendar',
    generateADatedMultiTouchProspecting: 'Generate a dated, multi-touch prospecting sequence from a goal and a prospect list, then schedule it as CRM reminders.',
    goalTakenFromYourProspectingStrategy: 'Goal taken from your prospecting strategy. Adjust it if needed, then target your prospects.',
    businessGoal: 'Business goal',
    eGProposeACoPromotion: 'E.g. propose a co-promotion partnership, present our program…',
    target: 'Target',
    contactIdentifiersSeparatedByCommasOr: 'Contact identifiers, separated by commas or line breaks (max 50)',
    crmGroupOrSegmentIdentifier: 'CRM group or segment identifier',
    channels: 'Channels',
    linkedinTouchesAre: 'LinkedIn touches are',
    manual2: 'manual',
    remindersNeverSentAutomatically: 'reminders — never sent automatically.',
    cadence: 'Cadence',
    windowDays: 'Window (days)',
    businessDaysOnly: 'Business days only',
    generating: 'Generating…',
    generateTheCalendar: 'Generate the calendar',
    yourRoleCannotGenerateACalendar: 'Your role cannot generate a calendar (the action uses AI credits) — contact an administrator.',
    generatingTheProspectingCalendar: 'Generating the prospecting calendar…',
    prospectS: 'prospect(s) ·',
    touchEs: 'touch(es)',
    checkComplianceCasl: 'Check compliance (CASL)',
    scheduling: 'Scheduling…',
    scheduleEveryTouch: 'Schedule every touch',
    reminderSCreated: 'reminder(s) created',
    blockedBlockedByCompliance: ' · {{blocked}} blocked by compliance',
    failedFailureS: ' · {{failed}} failure(s)',
    lengthTouchEs: '{{length}} touch(es)',
    touchTouchNoDDayOffset: 'Touch {{touch_no}} · D+{{day_offset}}',
    writingHintDraftHint: 'Writing hint: {{draft_hint}}',
    generateACalendarToScheduleA: 'Generate a calendar to schedule a dated, multi-touch prospecting sequence.',
  },
  prospectionStepNav: {
    offers: 'Offers',
    strategy: 'Strategy',
    discovery: 'Discovery',
    recommend: 'Recommend',
    calendar: 'Calendar',
    intelligence: 'Intelligence',
    visitors: 'Visitors',
    jobChanges: 'Job changes',
    inboundRouting: 'Inbound routing',
    prospectingSteps: 'Prospecting steps',
  },
  recommendPage: {
    productServiceOrGoal: 'Product / service or goal',
    sourceCrmOrDiscovery: 'Source (CRM or discovery)',
    discoveryQueryIfSourceDiscovery: 'Discovery query (if source = discovery)',
    numberOfRecommendations: 'Number of recommendations',
    theIcpRankingFailed: 'The ICP ranking failed.',
    describeTheProductServiceOrGoal: 'Describe the product, service or goal to target.',
    describeWhatTheDiscoveryShouldLook: 'Describe what the discovery should look for (name, industry, area…).',
    theRecommendationFailedTryAgain: 'The recommendation failed. Try again.',
    prospectingRecommendation: 'Prospecting · Recommendation',
    describeAProductOrServiceAnd: 'Describe a product or service and get the best contacts to add — from your CRM or freshly discovered prospects — ranked by relevance with the reason for each score.',
    eGAccountingSoftwareForSmbs: 'E.g. accounting software for SMBs, cybersecurity training…',
    prospectSource: 'Prospect source',
    existingContactsCrm: 'Existing contacts (CRM)',
    freshlyDiscoveredProspects: 'Freshly discovered prospects',
    stageOptionalAllContactsByDefault: 'Stage (optional — all contacts by default)',
    allStages: 'All stages',
    discoveryQuery: 'Discovery query',
    eGAccountingFirmsInMontreal: 'E.g. accounting firms in Montreal…',
    firstRunsADiscoveryAllAvailable: 'First runs a discovery (all available sources, up to 50 results), then scores each candidate for the goal above — nothing is saved until you click “Add to the CRM”.',
    analysisInProgress: 'Analysis in progress…',
    recommend: 'Recommend',
    yourRoleCannotRunARecommendation: 'Your role cannot run a recommendation (the action uses credits) — contact an administrator.',
    analyzingRelevance: 'Analyzing relevance…',
    aiUnavailableForThisRecommendationApprox: 'AI unavailable for this recommendation — approximate keyword scores (not verified by AI), clearly marked on each card.',
    noCandidateDiscoveredForThisQuery: 'No candidate discovered for this query — broaden the search.',
    noContactMatchesTheseFilters: 'No contact matches these filters.',
    noRecommendations: 'No recommendations.',
    prospectSAnalyzedLengthRecommended: 'prospect(s) analyzed · {{length}} recommended',
    aiScored: ' · AI-scored',
    addToTheCrmSize: 'Add to the CRM ({{size}})',
    selectV: 'Select {{v}}',
    engagementScoreLeadScore: 'Engagement score: {{lead_score}}',
    icpIcpScore: 'ICP: {{icp_score}}',
    source: 'Source:',
    confidenceV: 'Confidence {{v}}',
    describeAProductOrServiceAbove: 'Describe a product or service above, then click “Recommend”.',
    icpRankingRulesFree: 'ICP ranking (rules, free)',
    existingDeterministicRankingEngagementSc: 'Existing deterministic ranking (engagement score + configured target profile) — no AI, instant. Complements the semantic recommendation above.',
    loading: 'Loading…',
    refresh: 'Refresh',
    showTheIcpRanking: 'Show the ICP ranking',
    noContactRankedYet: 'No contact ranked yet.',
    contact: 'Contact',
    company: 'Company',
    engagement: 'Engagement',
    icp: 'ICP',
    total: 'Total',
    grade: 'Grade',
  },
  sourceStatus: {
    active: 'Active',
    notConfigured: 'Not configured',
    error: 'Error',
    limitReached: 'Limit reached',
    countResultSFound: '{{count}} result(s) found.',
    noResultsForTheseCriteria: 'No results for these criteria.',
    apiKeyNotConfiguredAddIt: 'API key not configured — add it in Settings > Integrations to enable this source.',
    quotaTemporarilyExceededTryAgainLater: 'Quota temporarily exceeded — try again later.',
    theCallToThisSourceFailed: 'The call to this source failed.',
  },
  sourceStatusPanel: {
    sourcesQueried: 'Sources queried',
  },
  strategyPage: {
    priorityPriority: 'Priority {{priority}}',
    angle: 'Angle:',
    typicalTargets: 'Typical targets',
    channels: 'Channels',
    createdOnV: 'Created on {{v}}',
    refV: '· ref. {{v}}',
    offer: 'Offer:',
    recommendedChannelMix: 'Recommended channel mix',
    cadence: 'Cadence',
    touchesTouchEs: '· {{touches}} touch(es)',
    windowDaysDayWindow: '· {{window_days}}-day window',
    generateTheCalendarFromThisStrategy: 'Generate the calendar from this strategy',
    targetSegmentsLength: 'Target segments ({{length}})',
    successIndicators: 'Success indicators',
    nextActions: 'Next actions',
    prospectProfileToLookFor: 'Prospect profile to look for',
    feedsTheDiscoveryStepUseIt: 'Feeds the “Discovery” step — use it as a search criterion.',
    prospectingGoal: 'Prospecting goal',
    productServiceCause: 'Product / service / cause',
    targetAudience: 'Target audience',
    constraints: 'Constraints',
    numberOfSegments: 'Number of segments',
    couldNotLoadTheSavedStrategies: 'Could not load the saved strategies.',
    describeTheBusinessGoalOfYour: 'Describe the business goal of your prospecting.',
    strategyDefinedAndSaved: 'Strategy defined and saved.',
    strategyDefinedNotSaved: 'Strategy defined (not saved).',
    theAiServiceIsTemporarilyUnavailable: 'The AI service is temporarily unavailable — try again in a moment (no strategy made up).',
    definingTheStrategyFailedTryAgain: 'Defining the strategy failed. Try again.',
    couldNotOpenThisStrategy: 'Could not open this strategy.',
    strategyDeleted: 'Strategy deleted.',
    prospectingStrategy: 'Prospecting · Strategy',
    turnABusinessGoalIntoA: 'Turn a business goal into a structured plan: target segments, message angles, channels, cadence and indicators — the step before the prospecting calendar.',
    prospectingStrategy2: 'Prospecting strategy',
    defineAStrategy: 'Define a strategy',
    myStrategies: 'My strategies',
    eGBuildPartnershipsToFund: 'E.g. build partnerships to fund our program, land 10 qualified meetings with accounting firms in Quebec…',
    productServiceCauseOptional: 'Product / service / cause (optional)',
    whatYouOffer: 'What you offer',
    constraintsOptional: 'Constraints (optional)',
    budgetTimelineGeographyTone: 'Budget, timeline, geography, tone…',
    targetAudienceOptional: 'Target audience (optional)',
    describeFreelyWhoYouWantTo: 'Describe freely who you want to reach (industries, sizes, roles…)',
    preferredChannelsOptionalOtherwiseTheAi: 'Preferred channels (optional — otherwise the AI suggests the mix)',
    segmentsToProduce: 'Segments to produce',
    saveTheStrategy: 'Save the strategy',
    defining: 'Defining…',
    defineTheStrategy: 'Define the strategy',
    yourRoleCannotDefineAStrategy: 'Your role cannot define a strategy (the action uses AI credits) — contact an administrator.',
    buildingTheProspectingStrategy: 'Building the prospecting strategy…',
    describeAGoalToGenerateA: 'Describe a goal to generate a structured prospecting strategy.',
    savedStrategiesMostRecentFirst: 'Saved strategies (most recent first).',
    refreshing: 'Refreshing…',
    refresh: 'Refresh',
    loadingStrategies: 'Loading strategies…',
    noSavedStrategyYetDefineOne: 'No saved strategy yet. Define one in the “Define a strategy” tab.',
    segmentS: 'segment(s)',
    opening: 'Opening…',
    view: 'View',
    deleteTheStrategyGoal: 'Delete the strategy “{{goal}}”',
    delete: 'Delete',
    deleteTheStrategy: 'Delete the strategy',
    permanentlyDeleteTheStrategyGoalThis: 'Permanently delete the strategy “{{goal}}”? This cannot be undone.',
  },
  visitorsPage: {
    company: 'Company',
    visits: 'Visits',
    uniqueVisitors: 'Unique visitors',
    lastVisit: 'Last visit',
    prospectingIdentifiedVisitors: 'Prospecting · Identified visitors',
    companiesIdentifiedBehindConsentedVisits: 'Companies identified behind consented visits to your website (reverse-IP resolution), with the number of visits and the last visit.',
    loadingIdentifiedVisitors: 'Loading identified visitors…',
    visitorIdentificationComingSoonNotConfig: 'Visitor identification — coming soon / not configured. This feature requires a provider key (reverse-IP) configured by the administrator, or is not deployed on this environment yet.',
    noIdentifiedVisitorYetCompaniesAppear: 'No identified visitor yet. Companies appear here as soon as a consented visit to your website is resolved to a known company.',
    vV2OfTotal: '{{v}}–{{v2}} of {{total}}',
    previous: 'Previous',
    next: 'Next',
  },
};

export default en;
